import React, { memo, useCallback } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import type { PipelineStage, ProjectStatus } from '../types';

import classes from './stageMappingEditor.module.css';

export interface StageMappingRow {
  rowKey: number;
  stageId: string;
  statusId: string;
  configId?: number;
}

interface StageMappingEditorProps {
  mappings: StageMappingRow[];
  stages: PipelineStage[];
  statuses: ProjectStatus[];
  excludeStageIds: string[];
  onAdd: () => void;
  onChange: (mappings: StageMappingRow[]) => void;
}

function StageMappingEditor({ mappings, stages, statuses, excludeStageIds, onAdd, onChange }: StageMappingEditorProps) {
  const usedStageIds = mappings.map((m) => m.stageId).filter(Boolean);

  const handleRemove = useCallback(
    (key: number) => {
      onChange(mappings.filter((m) => m.rowKey !== key));
    },
    [mappings, onChange]
  );

  const handleChange = useCallback(
    (key: number, field: 'stageId' | 'statusId', value: string) => {
      onChange(
        mappings.map((m) => {
          if (m.rowKey === key) {
            return { ...m, [field]: value };
          }
          return m;
        })
      );
    },
    [mappings, onChange]
  );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <label className="form-label fw-semibold mb-0">Stage Status Mappings</label>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={onAdd}
        >
          + Add Mapping
        </button>
      </div>

      {mappings.length === 0 && (
        <p className="text-muted small mb-0">
          No stage mappings configured. Add mappings to update project status when opportunities move to other stages.
        </p>
      )}

      {mappings.map((mapping) => (
        <div key={mapping.rowKey} className={classes.mappingRow}>
          <div className={classes.fieldSelect}>
            <select
              className="form-select form-select-sm"
              value={mapping.stageId}
              onChange={(e) => handleChange(mapping.rowKey, 'stageId', e.target.value)}
            >
              <option value="">Select stage...</option>
              {stages.map((stage) => (
                <option
                  key={stage.id}
                  value={stage.id}
                  disabled={
                    (excludeStageIds.includes(stage.id) || usedStageIds.includes(stage.id))
                    && mapping.stageId !== stage.id
                  }
                >
                  {stage.name}
                </option>
              ))}
            </select>
          </div>

          <span className={classes.arrow}>&rarr;</span>

          <div className={classes.fieldSelect}>
            <select
              className="form-select form-select-sm"
              value={mapping.statusId}
              onChange={(e) => handleChange(mapping.rowKey, 'statusId', e.target.value)}
            >
              <option value="">Select status...</option>
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className={`btn btn-outline-danger btn-sm ${classes.removeButton}`}
            onClick={() => handleRemove(mapping.rowKey)}
            title="Remove mapping"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}

const StageMappingEditorMemo = memo(StageMappingEditor, areEqual);

export { StageMappingEditorMemo as StageMappingEditor };
